"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiGrid } from "react-icons/fi";
import { AttachmentsGrid } from "@/components/scribe/attachments-grid";
import { DropdownMenuItem } from "@/components/ui/dropdown";
import { isLocalNoteId, useSelectedNote, useTemplateDetail } from "@/hooks/use-selected-note";
import { copyTemplateAnnotations, findScribeAttachmentsAnswerId, findScribeAttachmentsQuestion, getTemplateGalleryImages } from "@/lib/note-utils";
import { toast } from "@/lib/use-toast";
import { PlaceholderImage, QuestionType, ScribeAttachments } from "@/types/forms/question";
import { NoteAttachment, NoteDetail, NoteTemplateTableItem } from "@/types/notes";
import TemplateImageGallery from "./template-image-gallery";
import { getSignedUrl } from "@/api/auth/gcs";
import { ADD_FROM_URL_ERROR_TOAST_ID, useAnnotationMutation, useMutationQueries } from "@/api/provider/scribe/note-mutation";
import { noteQueryKeys, noteQueryOptions } from "@/api/provider/scribe/note-query";
import { updateNoteAnswers } from "@/api/provider/scribe/notes";

// Helper type for placeholder with signed URL
interface PlaceholderWithUrl extends PlaceholderImage {
  signedUrl?: string;
}

// Helper function to create placeholder attachment from template placeholder
const createPlaceholderAttachment = (placeholder: PlaceholderWithUrl): NoteAttachment => ({
  id: `template-placeholder-${placeholder.id}`,
  url: placeholder.signedUrl || "",
  signedUrl: placeholder.signedUrl,
  name: placeholder.name,
  isPlaceholder: true,
  gcsPath: placeholder.gcsPath,
  // Fresh copies, so the provider sees the template's marks and saving the
  // image creates them on the patient's copy along with any edits.
  annotations: copyTemplateAnnotations(placeholder.annotations)
});

// Helper function to extract placeholders from SCRIBE_ATTACHMENTS questions
const getPlaceholdersFromTemplate = (template: NoteTemplateTableItem | null | undefined): PlaceholderWithUrl[] => {
  if (!template?.questions) {
    return [];
  }

  // Find all SCRIBE_ATTACHMENTS questions and collect their placeholders
  const placeholders: PlaceholderWithUrl[] = [];
  for (const question of template.questions) {
    if (question.questionType === QuestionType.SCRIBE_ATTACHMENTS) {
      const data = question.data as ScribeAttachments;
      if (data?.placeholders) {
        // Images on a pick-from-gallery question only join the note once
        // picked. Empty photo slots still show as tiles to fill.
        placeholders.push(...data.placeholders.filter(p => !(data.pickFromGallery && p.gcsPath)));
      }
    }
  }
  return placeholders;
};

// Key under which dismissed template-placeholder names are persisted on a
// SCRIBE_ATTACHMENTS answer's free-form `data` JSON.
const DISMISSED_PLACEHOLDERS_KEY = "dismissedPlaceholders";

// Read the persisted set of dismissed placeholder names from the note's
// SCRIBE_ATTACHMENTS answers, so a deletion survives a reload.
const getPersistedDismissedNames = (note: NoteDetail | null | undefined, template: NoteTemplateTableItem | null | undefined): Set<string> => {
  const names = new Set<string>();
  if (!note?.answers || !template?.questions) return names;
  for (const question of template.questions) {
    if (question.questionType !== QuestionType.SCRIBE_ATTACHMENTS) continue;
    const answer = note.answers.find(a => a.questionId === question.id);
    const dismissed = answer?.data?.[DISMISSED_PLACEHOLDERS_KEY];
    if (Array.isArray(dismissed)) {
      for (const name of dismissed) {
        if (typeof name === "string") names.add(name);
      }
    }
  }
  return names;
};
interface NoteAttachmentSectionProps {
  disabled?: boolean;
  className?: string;
  createServerNote?: () => void;
  isManualNote?: boolean;
}
export default function NoteAttachmentSection({
  disabled = false,
  className
}: NoteAttachmentSectionProps) {
  const queryClient = useQueryClient();
  const {
    selectedNote
  } = useSelectedNote();
  const [templateId, setTemplateId] = useState<string | undefined>(selectedNote?.template?.id);
  const {
    templateDetail: selectedTemplate
  } = useTemplateDetail(templateId);
  const [convertingPlaceholderId, setConvertingPlaceholderId] = useState<string | null>(null);

  // Local state for template placeholders (with fetched signed URLs)
  const [localPlaceholders, setLocalPlaceholders] = useState<NoteAttachment[]>([]);

  // Track converted placeholder names to avoid filtering issues before API refresh
  const [convertedPlaceholderNames, setConvertedPlaceholderNames] = useState<Set<string>>(new Set());

  // Placeholders the provider deleted this session, before the persisted
  // dismissal round-trips back through the note detail query.
  const [sessionDeletedPlaceholderNames, setSessionDeletedPlaceholderNames] = useState<Set<string>>(new Set());
  useEffect(() => {
    setTemplateId(selectedNote?.template?.id);
    // Reset placeholder tracking when the note or template changes, so names
    // deleted in one note don't hide same-named placeholders in another.
    setConvertedPlaceholderNames(new Set());
    setSessionDeletedPlaceholderNames(new Set());
    // Gallery picks still saving belong to the note they were added on.
    setPendingGalleryAdds([]);
    setHiddenAttachmentIds(new Set());
  }, [selectedNote?.id, selectedNote?.template?.id]);

  // Names to keep hidden = persisted dismissals (survive reload) + this
  // session's deletions (hidden immediately, before the query refetch).
  const deletedPlaceholderNames = useMemo(() => {
    const persisted = getPersistedDismissedNames(selectedNote, selectedTemplate);
    if (sessionDeletedPlaceholderNames.size === 0) return persisted;
    return new Set([...Array.from(persisted), ...Array.from(sessionDeletedPlaceholderNames)]);
  }, [selectedNote, selectedTemplate, sessionDeletedPlaceholderNames]);
  const {
    addFromUrlMutation
  } = useMutationQueries();
  const annotationMutation = useAnnotationMutation(selectedNote?.patient?.id ?? "", selectedNote?.id && !isLocalNoteId(selectedNote.id) ? selectedNote.id : undefined);

  // Template-mark saves run one at a time, across every pick: the chart takes
  // its doses from the newest save, so overlapping saves would leave the
  // earlier images' doses off it until a refresh.
  const markSaveQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  // Copy a gallery image's template marks onto the note's new attachment. The
  // marks save through the normal annotation path, so their doses are charted
  // just as if the provider had drawn them.
  const applyTemplateAnnotations = (attachment: NoteAttachment, annotations: NoteAttachment["annotations"]): Promise<unknown> => {
    if (!annotations?.length) return Promise.resolve();
    // Captured now: the save may run after the provider has switched notes.
    const patientId = selectedNote?.patient?.id ?? "";
    const noteId = selectedNote?.id && !isLocalNoteId(selectedNote.id) ? selectedNote.id : null;
    const save = markSaveQueueRef.current.then(() => annotationMutation.mutateAsync({
      attachmentId: attachment.id,
      annotations,
      baseline: [],
      // The provider didn't click save, so no "saved" toast.
      silent: true,
      patientId,
      noteId
    }).catch(() => null));
    markSaveQueueRef.current = save;
    return save;
  };

  // Helper to ensure an answer exists for attachments
  const ensureScribeAttachmentsAnswer = async (placeholderName?: string): Promise<string | null> => {
    if (!selectedNote?.id || !selectedTemplate?.questions) return null;

    // 1. Try to find existing answer
    const answerId = findScribeAttachmentsAnswerId(selectedNote, selectedTemplate, placeholderName);
    if (answerId) return answerId;

    // 2. Find the target question
    const question = findScribeAttachmentsQuestion(selectedTemplate, placeholderName);
    const questionId = question?.id;
    if (!questionId) return null;

    // 3. Create temp answer. Annotating a template image is allowed *before* AI
    // generation runs (the UI keeps the attachment area editable pre-generation
    // so providers can mark up body/face diagrams during the visit). The backend
    // scribe revision path now carries the SCRIBE_ATTACHMENTS answer + attachment
    // forward into the generated revision (see scribe_note), so the annotated
    // image is no longer orphaned on the draft.
    try {
      const tempAnswerId = `temp-${questionId}`;
      const createdAnswers = await updateNoteAnswers(selectedNote.id, [{
        id: tempAnswerId,
        data: {} // Empty data for SCRIBE_ATTACHMENTS questions
      }]);
      if (createdAnswers && createdAnswers.length > 0) {
        // Find the newly created answer
        const newAnswer = createdAnswers.find(a => a.questionId === questionId);
        if (newAnswer) {
          queryClient.setQueryData<NoteDetail | undefined>(noteQueryKeys.detail(selectedNote.id), prev => prev ? {
            ...prev,
            answers: [...(prev.answers ?? []).filter(answer => !createdAnswers.some(created => created.id === answer.id)), ...createdAnswers]
          } : prev);

          // We return the ID directly from the API response rather than waiting for
          // the query refetch to populate it in the note detail state.
          return newAnswer.id;
        }
        console.warn(`Created answer for question ${questionId} not found in response`, createdAnswers);
      }
      // Reached when updateNoteAnswers returned an empty response or one that
      // omitted the answer we created — an API anomaly a retry may recover from.
      toast({
        title: "Error",
        description: "Something went wrong preparing this image. Please try again.",
        variant: "error"
      });
    } catch (error) {
      console.error("Failed to create attachment answer:", error);
      toast({
        title: "Error",
        description: "Could not create answer for attachment question. Please ensure the note is saved.",
        variant: "error"
      });
    }
    return null;
  };

  // Serializes dismissal writes: each PUT replaces the answer's `data`
  // wholesale, so overlapping writes could drop each other's names. Chaining
  // makes each write read the previous one's committed result before merging.
  const dismissalQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  // Persist a placeholder deletion onto its SCRIBE_ATTACHMENTS answer so the
  // blank template photo doesn't re-derive from the template on reload.
  const persistPlaceholderDismissalNow = async (placeholderName_0: string) => {
    if (!selectedNote?.id || isLocalNoteId(selectedNote.id)) return;
    const noteId_0 = selectedNote.id;
    const question_0 = findScribeAttachmentsQuestion(selectedTemplate, placeholderName_0);
    if (!question_0?.id) return;

    // Read the freshest note from the cache. Writes are serialized, so this
    // reflects the previous dismissal's committed result before we merge.
    const cachedNote = queryClient.getQueryData<NoteDetail>(noteQueryKeys.detail(noteId_0)) ?? selectedNote;
    const existingAnswer = cachedNote.answers?.find(a_0 => a_0.questionId === question_0.id);

    // Pre-generation the note has no answers; any non-archived answer is treated
    // as "generation ran" (hiding the Generate button). Don't create a
    // metadata-only answer just to record a dismissal — session state keeps the
    // deletion hidden until an answer exists to persist onto.
    if (!existingAnswer && (cachedNote.answers?.length ?? 0) === 0) return;

    // Reuse the existing answer id (data is replaced wholesale server-side) or a
    // temp id the backend upserts into a new answer.
    const answerId_0 = existingAnswer?.id ?? `temp-${question_0.id}`;
    const existingData = existingAnswer?.data ?? {};
    const prevDismissed = Array.isArray(existingData[DISMISSED_PLACEHOLDERS_KEY]) ? (existingData[DISMISSED_PLACEHOLDERS_KEY] as unknown[]).filter((n): n is string => typeof n === "string") : [];
    if (prevDismissed.includes(placeholderName_0)) return;

    // Merge so we don't clobber other keys the answer's data may hold.
    const nextData = {
      ...existingData,
      [DISMISSED_PLACEHOLDERS_KEY]: [...prevDismissed, placeholderName_0]
    };
    try {
      const updated = await updateNoteAnswers(noteId_0, [{
        id: answerId_0,
        data: nextData
      }]);
      // requestData swallows HTTP/network errors and resolves null, so an empty
      // result is a failure, not success — surface it rather than silently
      // leaving the placeholder hidden only in local state.
      if (!updated || updated.length === 0) {
        throw new Error("updateNoteAnswers returned no answers");
      }
      // Merge only the returned answers into the cache — never restore a whole
      // snapshot, so unrelated concurrent cache updates aren't clobbered.
      queryClient.setQueryData<NoteDetail | undefined>(noteQueryKeys.detail(noteId_0), prev_0 => prev_0 ? {
        ...prev_0,
        answers: [...(prev_0.answers ?? []).filter(a_1 => !updated.some(u => u.id === a_1.id)), ...updated]
      } : prev_0);
    } catch (error_0) {
      console.error("Failed to persist placeholder dismissal:", error_0);
      // The deletion wasn't saved — un-hide it so the provider sees it's back.
      setSessionDeletedPlaceholderNames(names => {
        if (!names.has(placeholderName_0)) return names;
        const next = new Set(names);
        next.delete(placeholderName_0);
        return next;
      });
      toast({
        title: "Error",
        description: "Couldn't save the removed template photo — it may reappear when the note is reopened.",
        variant: "error"
      });
    }
  };
  const persistPlaceholderDismissal = (placeholderName_1: string): Promise<void> => {
    const next_0 = dismissalQueueRef.current.catch(() => {}).then(() => persistPlaceholderDismissalNow(placeholderName_1));
    dismissalQueueRef.current = next_0;
    return next_0;
  };

  // Attachments query - fetch raw data for comparison modal
  const noteId_1 = selectedNote?.id;
  const validNoteId = noteId_1 && !isLocalNoteId(noteId_1) ? noteId_1 : undefined;
  const isQueryEnabled = !!validNoteId;
  const {
    data: rawAttachments = [],
    isLoading: noteAttachmentsLoading
  } = useQuery<NoteAttachment[] | null, Error, NoteAttachment[], ReturnType<typeof noteQueryKeys.attachments>>({
    ...noteQueryOptions.attachments(noteId_1 ?? ""),
    enabled: isQueryEnabled,
    select: data => data ?? []
  });

  // For local notes or when query is disabled, isLoading should be false
  const isLoading = isQueryEnabled ? noteAttachmentsLoading : false;

  // Transform to NoteAttachment for local use - memoized to prevent unnecessary effect triggers
  const attachments = useMemo(() => rawAttachments.map(a_2 => {
    const isPlaceholderAtt = a_2.id.startsWith("template-placeholder-");
    return {
      id: a_2.id,
      attachmentId: isPlaceholderAtt ? undefined : a_2.id,
      url: a_2.signedUrl || "",
      signedUrl: a_2.signedUrl,
      thumbnailSignedUrl: a_2.thumbnailSignedUrl,
      gcsPath: a_2.gcsPath,
      annotations: a_2.annotations,
      name: a_2.name,
      isPlaceholder: isPlaceholderAtt,
      tags: a_2.tags
    } as NoteAttachment;
  }), [rawAttachments]);

  // Upload template attachments when template changes: enqueue placeholders and then convert via mutation if needed
  useEffect(() => {
    const enqueueTemplatePlaceholders = async () => {
      if (!selectedNote?.id) return;

      // Get placeholders from SCRIBE_ATTACHMENTS questions
      const templatePlaceholders = getPlaceholdersFromTemplate(selectedTemplate);
      if (templatePlaceholders.length === 0) {
        // Only clear if there are existing placeholders to avoid infinite loop
        setLocalPlaceholders(prev_1 => prev_1.length > 0 ? [] : prev_1);
        return;
      }

      // Get real attachments (not placeholders) that already exist
      const existingRealAttachments = attachments.filter(att => !att.isPlaceholder);
      // Use names for matching - placeholder names are preserved when converted
      const existingNames = new Set(existingRealAttachments.map(att_0 => att_0.name).filter(Boolean));

      // Filter out placeholders that have already been converted to real attachments
      // Compare by name - the original placeholder name is preserved on the attachment
      const newTemplateAttachments = templatePlaceholders.filter(templateAtt => {
        // Skip if locally tracked as converted (by name)
        if (convertedPlaceholderNames.has(templateAtt.name)) return false;
        // Skip if the provider deleted it (persisted or this session)
        if (deletedPlaceholderNames.has(templateAtt.name)) return false;
        // Skip if name matches an existing attachment
        if (existingNames.has(templateAtt.name)) return false;
        return true;
      });
      if (newTemplateAttachments.length === 0) {
        // Only clear if there are existing placeholders to avoid infinite loop
        setLocalPlaceholders(prev_2 => prev_2.length > 0 ? [] : prev_2);
        return;
      }

      // Fetch signed URLs for placeholders that have gcsPath but no signedUrl
      const placeholdersWithUrls = await Promise.all(newTemplateAttachments.map(async placeholder => {
        // If already has a valid signedUrl (not empty), use it
        if (placeholder.signedUrl && placeholder.signedUrl.startsWith("http")) {
          return placeholder;
        }

        // If has gcsPath, fetch signed URL
        if (placeholder.gcsPath) {
          try {
            const response = await getSignedUrl(placeholder.gcsPath);
            if (response?.signedUrl) {
              return {
                ...placeholder,
                signedUrl: response.signedUrl
              };
            }
          } catch {
            // Failed to fetch signed URL, continue with placeholder as-is
          }
        }

        // Return placeholder as-is if no URL could be fetched
        return placeholder;
      }));

      // Create placeholder attachments and store in local state
      const placeholders = placeholdersWithUrls.map(createPlaceholderAttachment);

      // Only update if the placeholders are different (compare by IDs)
      setLocalPlaceholders(prev_3 => {
        const prevIds = new Set(prev_3.map(p => p.id));
        const newIds = new Set(placeholders.map(p_0 => p_0.id));
        const isSame = prevIds.size === newIds.size && Array.from(prevIds).every(id => newIds.has(id));
        return isSame ? prev_3 : placeholders;
      });
    };
    enqueueTemplatePlaceholders();
  }, [selectedTemplate?.id, selectedNote?.id, attachments, convertedPlaceholderNames, deletedPlaceholderNames]);
  const isPlaceholder = (attachment_0: NoteAttachment): boolean => {
    return attachment_0.isPlaceholder === true;
  };

  // Convert Type 1 placeholder (with image) to real attachment
  const convertPlaceholderToRealAttachment = async (attachment_1: NoteAttachment): Promise<NoteAttachment | null> => {
    if (!attachment_1.url || !attachment_1.url.startsWith("http")) {
      console.error("Cannot convert placeholder: invalid URL", attachment_1.url);
      toast({
        title: "Error",
        description: "This template image can't be annotated.",
        variant: "error"
      });
      return null;
    }
    // A local (unpersisted) note id is truthy but not a real note: creating the
    // SCRIBE_ATTACHMENTS answer against it would fail. Guard so we never call
    // updateNoteAnswers with an unpersisted id.
    if (!selectedNote?.id || isLocalNoteId(selectedNote.id)) {
      console.error("Cannot convert placeholder: no persisted note ID");
      toast({
        title: "Error",
        description: "Please save the note first before annotating",
        variant: "error"
      });
      return null;
    }
    try {
      // Find which SCRIBE_ATTACHMENTS question this placeholder belongs to (by name)
      const placeholderName_2 = attachment_1.name;
      if (!placeholderName_2) {
        console.error("Missing name for placeholder attachment:", attachment_1);
        toast({
          title: "Error",
          description: "This template image can't be annotated.",
          variant: "error"
        });
        return null;
      }

      // Find the SCRIBE_ATTACHMENTS question that contains this placeholder and get its gcsPath
      const question_1 = findScribeAttachmentsQuestion(selectedTemplate, placeholderName_2);
      if (!question_1) {
        toast({
          title: "Error",
          description: "Could not find attachment question for this placeholder",
          variant: "error"
        });
        return null;
      }
      const data_0 = question_1.data as ScribeAttachments;
      const placeholder_0 = data_0?.placeholders?.find(p_1 => p_1.name === placeholderName_2);
      const placeholderGcsPath = placeholder_0?.gcsPath;

      // Prefer gcsPath over url to avoid expired signed URLs
      const gcsPath = attachment_1.gcsPath || placeholderGcsPath;

      // Find or create the answer ID
      const answerId_1 = await ensureScribeAttachmentsAnswer(placeholderName_2);
      if (!answerId_1) {
        // ensureScribeAttachmentsAnswer surfaces its own error toast on every
        // failure path it returns null from, so we just bail here.
        return null;
      }

      // Use the new API endpoint to directly download and create attachment from GCS
      if (!gcsPath) {
        toast({
          title: "Error",
          description: "Could not find GCS path for placeholder image",
          variant: "error"
        });
        return null;
      }
      const response_0 = await addFromUrlMutation.mutateAsync({
        gcsPath: gcsPath,
        name: attachment_1.name,
        answerId: answerId_1
      });
      if (response_0) {
        // Track this placeholder as converted to prevent filtering issues
        setConvertedPlaceholderNames(prev_4 => {
          const next_1 = new Set(prev_4);
          next_1.add(placeholderName_2);
          return next_1;
        });
        const newAttachment: NoteAttachment = {
          id: response_0.id,
          attachmentId: response_0.id,
          url: response_0.signedUrl,
          annotations: response_0.annotations,
          name: response_0.name
        };
        return newAttachment;
      }
      // The create-from-URL call resolved without an attachment — an API anomaly
      // a retry may recover from.
      toast({
        title: "Error",
        description: "Something went wrong preparing this image. Please try again.",
        variant: "error"
      });
      return null;
    } catch (error_1) {
      console.error("Failed to convert placeholder to real attachment:", error_1);
      toast({
        title: "Error",
        description: "Failed to prepare attachment for annotation",
        variant: "error"
      });
      return null;
    }
  };
  const galleryImages = useMemo(() => getTemplateGalleryImages(selectedTemplate), [selectedTemplate]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  // Gallery picks shown straight away, with their marks, while the image is
  // copied and the marks saved. The temp- id keeps them from being clicked
  // until the copy exists to annotate.
  const [pendingGalleryAdds, setPendingGalleryAdds] = useState<NoteAttachment[]>([]);
  // Copies that have landed but whose marks are still saving, hidden so the
  // image doesn't show up blank beside its pending tile.
  const [hiddenAttachmentIds, setHiddenAttachmentIds] = useState<Set<string>>(new Set());
  const attachmentNames = useMemo(() => new Set([...attachments, ...pendingGalleryAdds].map(a_3 => a_3.name).filter(Boolean) as string[]), [attachments, pendingGalleryAdds]);
  const addGalleryImages = async (images: PlaceholderImage[]) => {
    if (!validNoteId || images.length === 0) return;
    const batch = Date.now();
    const pending = images.map((image): NoteAttachment => ({
      id: `temp-gallery-${batch}-${image.id}`,
      url: image.signedUrl || "",
      signedUrl: image.signedUrl,
      gcsPath: image.gcsPath,
      name: image.name,
      annotations: copyTemplateAnnotations(image.annotations)
    }));
    const pendingIds = new Set(pending.map(p_2 => p_2.id));
    setPendingGalleryAdds(prev_5 => [...prev_5, ...pending]);
    const dropPending = (id_0: string) => setPendingGalleryAdds(prev_6 => prev_6.filter(p_3 => p_3.id !== id_0));
    const setHidden = (id_1: string, hidden: boolean) => setHiddenAttachmentIds(prev_7 => {
      const next_2 = new Set(prev_7);
      if (hidden) next_2.add(id_1);else next_2.delete(id_1);
      return next_2;
    });

    // A template has one attachments question, so one answer holds them all.
    const answerId_2 = await ensureScribeAttachmentsAnswer(images[0].name);
    if (!answerId_2) {
      setPendingGalleryAdds(prev_8 => prev_8.filter(p_4 => !pendingIds.has(p_4.id)));
      return;
    }
    // allSettled lets the rest finish when one image fails.
    const results = await Promise.allSettled(images.map(async (image_0, index) => {
      const attachment_2 = await addFromUrlMutation.mutateAsync({
        gcsPath: image_0.gcsPath,
        name: image_0.name,
        answerId: answerId_2
      }).catch(error_2 => {
        dropPending(pending[index].id);
        throw error_2;
      });
      if (attachment_2 && pending[index].annotations?.length) {
        setHidden(attachment_2.id, true);
        await applyTemplateAnnotations(attachment_2, pending[index].annotations);
        // The saved revision has a new id; unhiding the old one only matters
        // when the save failed and the bare copy is what's left.
        setHidden(attachment_2.id, false);
      }
      dropPending(pending[index].id);
      return attachment_2;
    }));
    const failed = results.filter(r => r.status === "rejected").length;
    if (failed > 0 && images.length > 1) {
      toast({
        id: ADD_FROM_URL_ERROR_TOAST_ID,
        title: "Error",
        description: `Couldn't add ${failed} of ${images.length} images.`,
        variant: "error"
      });
    }
  };

  // Transform to AttachmentsGrid format - merge server attachments with local placeholders
  const gridAttachments = useMemo(() => {
    // Transform server attachments
    const serverAttachments = attachments.map(a_4 => {
      const rawAttachment = rawAttachments.find(ra => ra.id === a_4.id);
      return {
        id: a_4.id,
        attachmentId: a_4.attachmentId,
        url: a_4.url || a_4.signedUrl,
        signedUrl: a_4.signedUrl || a_4.url,
        thumbnailSignedUrl: a_4.thumbnailSignedUrl,
        gcsPath: a_4.gcsPath,
        annotations: a_4.annotations || [],
        name: a_4.name || "",
        date: rawAttachment?.createdDate,
        uploadedAt: rawAttachment?.uploadedAt,
        isPlaceholder: a_4.isPlaceholder,
        tags: a_4.tags
      };
    });

    // Transform local placeholders
    const placeholderAttachments = localPlaceholders.map(a_5 => ({
      id: a_5.id,
      attachmentId: a_5.attachmentId,
      url: a_5.url || a_5.signedUrl,
      signedUrl: a_5.signedUrl || a_5.url,
      thumbnailSignedUrl: a_5.thumbnailSignedUrl,
      gcsPath: a_5.gcsPath,
      annotations: a_5.annotations || [],
      name: a_5.name || "",
      date: a_5.createdDate,
      isPlaceholder: a_5.isPlaceholder,
      tags: a_5.tags
    }));

    // Filter out placeholders that have already been converted
    // Compare by name - the original placeholder name is preserved on the attachment
    const serverNames = new Set(serverAttachments.map(a_6 => a_6.name).filter(Boolean));
    const filteredPlaceholders = placeholderAttachments.filter(p_5 => {
      // If this placeholder name was locally tracked as converted, filter it out
      if (p_5.name && convertedPlaceholderNames.has(p_5.name)) {
        return false;
      }

      // If the provider deleted it (persisted or this session), filter it out
      if (p_5.name && deletedPlaceholderNames.has(p_5.name)) {
        return false;
      }

      // If a real attachment exists with this name, filter it out
      if (p_5.name && serverNames.has(p_5.name)) {
        return false;
      }
      return true;
    });

    // New attachments land at the front of the list, so pending picks do too.
    const pendingAttachments = pendingGalleryAdds.map(a_7 => ({
      id: a_7.id,
      url: a_7.url,
      signedUrl: a_7.signedUrl,
      gcsPath: a_7.gcsPath,
      annotations: a_7.annotations || [],
      name: a_7.name || ""
    }));
    return [...pendingAttachments, ...serverAttachments.filter(a_8 => !hiddenAttachmentIds.has(a_8.id)), ...filteredPlaceholders];
  }, [attachments, rawAttachments, localPlaceholders, convertedPlaceholderNames, deletedPlaceholderNames, pendingGalleryAdds, hiddenAttachmentIds]);
  return <>
      <TemplateImageGallery open={isGalleryOpen} onOpenChange={setIsGalleryOpen} images={galleryImages} addedNames={attachmentNames} onConfirm={images_0 => void addGalleryImages(images_0)} />
      <AttachmentsGrid attachments={gridAttachments} isLoading={isLoading} size="lg" groupByDate={false} showUpload={!disabled && !!selectedNote?.id} uploadDisabled={disabled || !selectedNote?.id} patientId={selectedNote?.patient?.id} noteId={validNoteId || null} appointmentId={selectedNote?.eventId} dropdownActions={galleryImages.length > 0 && !disabled && validNoteId && <DropdownMenuItem onClick={() => setIsGalleryOpen(true)} icon={<FiGrid className="h-4 w-4" />}>
              Add template images
            </DropdownMenuItem>} onImageReplaced={() => {
      // Invalidate attachments query to refresh with new image
      queryClient.invalidateQueries({
        queryKey: noteQueryKeys.attachments({
          noteId: selectedNote?.id ?? ""
        })
      });
    }} className={className} placeholderControls={{
      isPlaceholder,
      convertingPlaceholderId,
      setConvertingPlaceholderId,
      convertPlaceholderToRealAttachment,
      onPlaceholderDelete: attachmentId => {
        const deletedName = localPlaceholders.find(p_6 => p_6.id === attachmentId)?.name;
        if (deletedName) {
          // Hide it now (before the refetch)...
          setSessionDeletedPlaceholderNames(names_0 => {
            const next_3 = new Set(names_0);
            next_3.add(deletedName);
            return next_3;
          });
          // ...and persist so it stays gone across reloads.
          void persistPlaceholderDismissal(deletedName);
        }
        setLocalPlaceholders(prev_9 => prev_9.filter(p_7 => p_7.id !== attachmentId));
      }
    }} showNoteLink={false} clickToAnnotate />
    </>;
}